import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectCurrentUser } from '../features/auth/authSlice';
import { usePlatformAccessLevel } from '../features/platformPermissions/usePlatformAccessLevel';
import { ExportPdfButton } from '../features/reports/ExportPdfButton';
import { ORG_WIDE_REPORT_RESOURCE_ID } from '../features/reports/types';
import { SoaControlsGrid } from '../features/iso27001/soa/SoaControlsGrid';
import { exportSoa, fetchSoaControls, updateSoaControl } from '../features/iso27001/soa/soaApi';
import type { IsoItemApplicability, SoaControlItem, SoaCoverageSummary } from '../features/iso27001/soa/types';
import { SOA_THEME_LABEL } from '../features/iso27001/soa/soaUtils';
import { showToast } from '../components/ui/toast.utils';

function isImplementedOrVerified(control: SoaControlItem): boolean {
  return (
    control.applicability === 'aplica' &&
    (control.implementationStatus === 'implementado' ||
      control.implementationStatus === 'verificado')
  );
}

function updateSummary(
  current: SoaCoverageSummary | null,
  before: SoaControlItem,
  after: SoaControlItem,
): SoaCoverageSummary | null {
  if (!current) return current;

  const next = {
    ...current,
    byTheme: {
      ...current.byTheme,
      [before.theme]: { ...current.byTheme[before.theme] },
    },
  };
  const theme = next.byTheme[before.theme];

  if (before.applicability !== after.applicability) {
    if (before.applicability === 'aplica') next.applicableCount -= 1;
    if (before.applicability === 'no_aplica') next.excludedCount -= 1;
    if (before.applicability === 'sin_definir') next.undefinedCount -= 1;
    if (after.applicability === 'aplica') next.applicableCount += 1;
    if (after.applicability === 'no_aplica') next.excludedCount += 1;
    if (after.applicability === 'sin_definir') next.undefinedCount += 1;

    if (before.applicability === 'no_aplica') theme.excluded -= 1;
    if (before.applicability === 'sin_definir') theme.undefined -= 1;
    if (after.applicability === 'no_aplica') theme.excluded += 1;
    if (after.applicability === 'sin_definir') theme.undefined += 1;
  }

  if (isImplementedOrVerified(before)) {
    next.implementedOrVerifiedCount -= 1;
    theme.implementedOrVerified -= 1;
  }
  if (isImplementedOrVerified(after)) {
    next.implementedOrVerifiedCount += 1;
    theme.implementedOrVerified += 1;
  }

  next.implementedOrVerifiedPercent =
    next.applicableCount > 0
      ? Math.round((next.implementedOrVerifiedCount / next.applicableCount) * 100)
      : 0;
  next.coveragePercent = next.implementedOrVerifiedPercent;
  return next;
}

export function SoaPage() {
  const { canEdit } = usePlatformAccessLevel('iso27001.soa.access');
  const user = useSelector(selectCurrentUser);
  const isSuperadmin = user?.role === 'superadmin';
  const [controls, setControls] = useState<SoaControlItem[]>([]);
  const [summary, setSummary] = useState<SoaCoverageSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [filterTheme, setFilterTheme] = useState('');
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchSoaControls({
        search: search.trim() || undefined,
        theme: filterTheme || undefined,
      });
      setControls(result.data);
      setSummary(result.summary);
    } catch {
      setError('No se pudo cargar la Declaración de Aplicabilidad.');
    } finally {
      setLoading(false);
    }
  }, [search, filterTheme]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const payload = await exportSoa();
      const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `soa-export-${new Date().toISOString().slice(0, 10)}.json`;
      anchor.click();
      URL.revokeObjectURL(url);
    } catch {
      setError('No se pudo exportar la SoA.');
    } finally {
      setExporting(false);
    }
  };

  const applyUpdatedControl = (
    control: SoaControlItem,
    patch: Partial<SoaControlItem>,
  ) => {
    const updatedControl: SoaControlItem = {
      ...control,
      ...patch,
      linkedRisks: patch.linkedRisks ?? control.linkedRisks,
    };

    setControls((current) =>
      current.map((item) => (item.id === control.id ? updatedControl : item)),
    );
    setSummary((current) => updateSummary(current, control, updatedControl));
  };

  const saveControlPatch = async (
    control: SoaControlItem,
    payload: Parameters<typeof updateSoaControl>[1],
  ) => {
    const updated = await updateSoaControl(control.id, payload);
    applyUpdatedControl(control, updated);
    showToast('Control SoA actualizado correctamente.', 'success');
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Declaración de Aplicabilidad (SoA)</h1>
          <p className="text-muted-foreground text-sm">
            93 controles del Anexo A:2022 — ISO 27001 (fuente única, sin duplicado en hoja de ruta)
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <ExportPdfButton
            reportType="soa"
            resourceId={ORG_WIDE_REPORT_RESOURCE_ID}
            permissionKey="iso27001.soa.access"
            disabled={loading}
          />
          <button
            type="button"
            className="btn btn-outline"
            onClick={() => void handleExport()}
            disabled={exporting || loading}
          >
            {exporting ? 'Exportando…' : 'Exportar SoA (JSON)'}
          </button>
        </div>
      </div>

      {summary && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
          <StatCard label="Controles totales" value={String(summary.totalControls)} />
          <StatCard label="Aplica" value={String(summary.applicableCount)} />
          <StatCard label="No aplica" value={String(summary.excludedCount)} />
          <StatCard label="Sin definir" value={String(summary.undefinedCount)} />
          <StatCard
            label="% cobertura (impl./verif.)"
            value={`${summary.implementedOrVerifiedPercent}%`}
          />
        </div>
      )}

      <div className="grid gap-3 md:grid-cols-2">
        <select
          className="input w-full"
          value={filterTheme}
          onChange={(e) => setFilterTheme(e.target.value)}
        >
          <option value="">Todos los temas</option>
          {Object.entries(SOA_THEME_LABEL).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
        <input
          className="input w-full"
          placeholder="Buscar por ref. o título…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {error && <p className="text-destructive text-sm">{error}</p>}

      {loading ? (
        <p className="text-muted-foreground text-sm">Cargando controles…</p>
      ) : (
        <SoaControlsGrid
          controls={controls}
          canEdit={canEdit}
          isSuperadmin={isSuperadmin}
          onApplicabilityChange={async (control, applicability: IsoItemApplicability, justification) => {
            await saveControlPatch(control, { applicability, justification });
          }}
          onStatusChange={async (control, implementationStatus) => {
            await saveControlPatch(control, { implementationStatus });
          }}
          onEvidenceChange={async (control, evidenceNote) => {
            await saveControlPatch(control, { evidenceNote });
          }}
          onOrgContextChange={
            isSuperadmin
              ? async (control, payload) => {
                  await saveControlPatch(control, payload);
                }
              : undefined
          }
        />
      )}
    </div>
  );
}

function StatCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-card border border-border rounded-lg p-4">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="text-2xl font-semibold mt-1">{value}</p>
    </div>
  );
}
